import React, { useState, useMemo, useRef } from "react";
import { Card } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { colors } from "utils";
import {
  findSelectionByCode,
  frameworkBadgeLabel,
  getGradeLabel,
  getResultBreadcrumb,
  standardsFrameworkId,
} from "utils/standards";
import { logError, LogCategory } from "utils/sentryLogger";
import { ExampleQuestionsCarousel } from "components/shared/ExampleQuestionsCarousel/ExampleQuestionsCarousel";
import { fetchConsensusQuestionsPage } from "utils/consensusQuestions";
import "./groupCodeStandardSearch.css";
import arrow_down from "assets/Arrow_Down.svg";
import arrow_right from "assets/Arrow_Right.svg";
import { SEARCH_TYPE, useSearchResultTracking } from "hooks/semanticSearchAnalytics";

// A result's entity_type as the analytics node_level.
const RESULT_NODE_LEVELS = { grade: "Grade", domain: "Domain", cluster: "Cluster", standard: "Standard" };

export const SearchResultsView = ({
  searchResults,
  onResultClick,
  onBackClick,
  standardsData = null,
  showZeroCountStandards = false,
  activeSchool = null,
}) => {
  const trackResultClick = useSearchResultTracking(SEARCH_TYPE.STANDARD);
  const frameworkId = standardsFrameworkId(activeSchool);
  const isStateFramework = Boolean(frameworkId);
  const frameworkBadge = frameworkBadgeLabel(frameworkId);
  const allowZeroCount = showZeroCountStandards || isStateFramework;

  const [selectedGrades, setSelectedGrades] = useState([]);
  const [expandedResults, setExpandedResults] = useState({});
  const [resultQuestions, setResultQuestions] = useState({});
  const [resultQuestionTotals, setResultQuestionTotals] = useState({});
  const [isLoadingQuestions, setIsLoadingQuestions] = useState({});
  const [isLoadingMoreQuestions, setIsLoadingMoreQuestions] = useState({});

  // Extract unique grades from results
  const availableGrades = useMemo(() => {
    const grades = new Set();
    searchResults.forEach(result => {
      if (result.grade) {
        grades.add(result.grade);
      }
    });
    return Array.from(grades).sort((a, b) => {
      // Sort grades: K first, then numbers, then HS
      if (a === 'K') return -1;
      if (b === 'K') return 1;
      if (a === 'HS') return 1;
      if (b === 'HS') return -1;
      return parseInt(a) - parseInt(b);
    });
  }, [searchResults]);

  // Filter results by selected grades
  const filteredResults = useMemo(() => {
    if (selectedGrades.length === 0) {
      return searchResults;
    }
    return searchResults.filter(result =>
      result.grade && selectedGrades.includes(result.grade)
    );
  }, [searchResults, selectedGrades]);

  const toggleGrade = (grade) => {
    setSelectedGrades(prev => {
      if (prev.includes(grade)) {
        return prev.filter(g => g !== grade);
      } else {
        return [...prev, grade];
      }
    });
  };

  const getEntityTypeLabel = (entityType) => {
    switch (entityType) {
      case 'standard':
        return 'STANDARD';
      case 'cluster':
        return 'CLUSTER';
      case 'domain':
        return 'DOMAIN';
      case 'grade':
        return 'GRADE';
      default:
        return entityType.toUpperCase();
    }
  };

  const getResultKey = (result) => {
    return `${result.entity_type}-${result.code}`;
  };

  // Grade results page by grade; everything else pages by its code.
  const getResultPrefix = (result) =>
    result.entity_type === "grade" ? result.grade : result.code;

  // Guards each result's fetch/loadMore against the OKO2-3534 request-fan-out
  // failure mode: `questionsInFlightRef` (keyed by resultKey) dedupes a re-entrant
  // fetchQuestionsForResult() call while one is already in flight for that result
  // (e.g. collapse then re-expand before the first resolves), and
  // `requestIdByResultRef` is a per-result monotonic counter so that if two
  // overlapping fetches for the same result resolve out of order, only the
  // response for the most-recently-started request is written to state.
  const questionsInFlightRef = useRef(new Map());
  const requestIdByResultRef = useRef(new Map());

  const nextRequestId = (resultKey) => {
    const next = (requestIdByResultRef.current.get(resultKey) || 0) + 1;
    requestIdByResultRef.current.set(resultKey, next);
    return next;
  };

  const fetchQuestionsForResult = async (result) => {
    const resultKey = getResultKey(result);
    let inFlight = questionsInFlightRef.current.get(resultKey);
    if (!inFlight) {
      inFlight = fetchConsensusQuestionsPage({ prefix: getResultPrefix(result) }).finally(() => {
        questionsInFlightRef.current.delete(resultKey);
      });
      questionsInFlightRef.current.set(resultKey, inFlight);
    }
    const myRequestId = nextRequestId(resultKey);
    setIsLoadingQuestions(prev => ({ ...prev, [resultKey]: true }));

    try {
      const { questions: pageQuestions, total } = await inFlight;
      if (requestIdByResultRef.current.get(resultKey) === myRequestId) {
        setResultQuestions(prev => ({ ...prev, [resultKey]: pageQuestions }));
        setResultQuestionTotals(prev => ({ ...prev, [resultKey]: total }));
      }
    } catch (e) {
      console.error("Error fetching questions:", e);
      logError("Failed to fetch search result questions", e, {
        category: LogCategory.ERROR,
      });
      if (requestIdByResultRef.current.get(resultKey) === myRequestId) {
        setResultQuestions(prev => ({ ...prev, [resultKey]: [] }));
        setResultQuestionTotals(prev => ({ ...prev, [resultKey]: 0 }));
      }
    } finally {
      if (requestIdByResultRef.current.get(resultKey) === myRequestId) {
        setIsLoadingQuestions(prev => ({ ...prev, [resultKey]: false }));
      }
    }
  };

  // Append the next page for a result's carousel once its end is reached.
  const loadMoreQuestionsForResult = async (result) => {
    const resultKey = getResultKey(result);
    const loaded = (resultQuestions[resultKey] || []).length;
    const total = resultQuestionTotals[resultKey] || 0;
    if (isLoadingMoreQuestions[resultKey] || loaded >= total) return [];
    const myRequestId = nextRequestId(resultKey);
    setIsLoadingMoreQuestions(prev => ({ ...prev, [resultKey]: true }));
    try {
      const { questions: pageQuestions, total: freshTotal } = await fetchConsensusQuestionsPage({
        prefix: getResultPrefix(result),
        offset: loaded,
      });
      if (requestIdByResultRef.current.get(resultKey) === myRequestId) {
        setResultQuestions(prev => ({ ...prev, [resultKey]: [...(prev[resultKey] || []), ...pageQuestions] }));
        setResultQuestionTotals(prev => ({ ...prev, [resultKey]: freshTotal }));
      }
      return pageQuestions;
    } catch (e) {
      logError("Failed to fetch more search result questions", e, {
        category: LogCategory.ERROR,
      });
      return [];
    } finally {
      if (requestIdByResultRef.current.get(resultKey) === myRequestId) {
        setIsLoadingMoreQuestions(prev => ({ ...prev, [resultKey]: false }));
      }
    }
  };

  const toggleResultQuestions = (result) => {
    const resultKey = getResultKey(result);
    const isExpanded = expandedResults[resultKey];
    setExpandedResults(prev => ({ ...prev, [resultKey]: !isExpanded }));

    // Fetch questions if expanding, not already loaded (or the last attempt came back
    // empty/errored), and not already loading.
    if (
      !isExpanded &&
      (!resultQuestions[resultKey] || resultQuestions[resultKey].length === 0) &&
      !isLoadingQuestions[resultKey]
    ) {
      fetchQuestionsForResult(result);
    }
  };

  // The server's search results carry no count; read it off the loaded tree
  // node instead, via the same lookup handleViewStandard uses to restore a
  // selection. findSelectionByCode's synthetic domain/cluster entity omits
  // `count`, so read it off the raw tree node it also returns.
  const treeNodeCount = (found) => {
    if (!found) return undefined;
    if (found.entity.type === "domain") return found.domain?.count;
    if (found.entity.type === "cluster") return found.cluster?.count;
    return found.entity?.count;
  };

  const handleViewStandard = (result, e, position) => {
    e.stopPropagation();
    trackResultClick(position, {
      standard_code: result.code,
      node_level: RESULT_NODE_LEVELS[result.entity_type || "standard"],
    });
    if (allowZeroCount && standardsData) {
      const found = findSelectionByCode(standardsData, result.code, true);
      if (found) {
        onResultClick(found.entity, found.grade, found.domain, found.cluster);
        return;
      }
    }
    const entity = {
      code: result.code,
      name: result.name,
      description: result.description,
      ...(result.entity_type !== "standard" ? { type: result.entity_type } : {})
    };

    // Call the parent handler with the entity and context
    onResultClick(entity, result.grade,
      result.domain_code ? { code: result.domain_code, name: result.domain_name } : null,
      result.cluster_code ? { code: result.cluster_code, name: result.cluster_name } : null
    );
  };

  return (
    <div className="search-results-view">
      <div className="search-results-header">
        <button className="back-to-tree-button" onClick={onBackClick}>
          <FontAwesomeIcon icon={faChevronLeft} />
          Search Results
        </button>
      </div>
      {/* Grade filter buttons */}
      {availableGrades.length > 0 && (
        <div className="grade-filter-container">
          {availableGrades.map(grade => (
            <button
              key={grade}
              className={`grade-filter-button ${selectedGrades.includes(grade) ? 'active' : ''}`}
              onClick={() => toggleGrade(grade)}
            >
              {getGradeLabel(grade).toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {/* Results list */}
      {filteredResults.length === 0 ? (
        <div className="no-results">
          {selectedGrades.length > 0
            ? `No results found for selected grade${selectedGrades.length > 1 ? 's' : ''}.`
            : 'No results found.'}
        </div>
      ) : (
        filteredResults.map((result, index) => {
          const resultKey = getResultKey(result);
          const isExpanded = expandedResults[resultKey];
          const questions = resultQuestions[resultKey] || [];
          const questionsTotal = resultQuestionTotals[resultKey] || questions.length;
          const isLoading = isLoadingQuestions[resultKey];
          const entityType = result.entity_type || "standard";
          const treeMatch = isStateFramework && standardsData
            ? findSelectionByCode(standardsData, result.code, true)
            : null;
          const count = treeNodeCount(treeMatch);
          const hasCount = count !== undefined;
          const isZeroCount = hasCount && count === 0;
          const zeroCountWarning = `0 questions are currently mapped to this ${entityType}. You can still assign it to a group.`;
          const breadcrumb = isStateFramework ? getResultBreadcrumb(result) : null;

          if (isStateFramework) {
            return (
              <div key={index} className="search-result-card">
                {/* Result Header */}
                <div className="standard-header">
                  <div className="standard-badge-container">
                    <div className="standard-type-badge" style={{ padding: 0 }}>
                      <span className="standard-code" onClick={(e) => handleViewStandard(result, e, index + 1)}>
                        {(frameworkBadge || getEntityTypeLabel(result.entity_type))} - {result.code}
                      </span>
                      <span className="standard-title-sm" onClick={(e) => handleViewStandard(result, e, index + 1)}>
                        {result.name || result.description}
                      </span>
                    </div>
                  </div>
                </div>

                {breadcrumb && (
                  <div className="breadcrumb-text" style={{ marginTop: "4px", marginBottom: "6px" }}>
                    {breadcrumb}
                  </div>
                )}

                {/* Description Section */}
                {result.description && (
                  <div className="description-section">
                    <p className="description-text">{result.description}</p>
                  </div>
                )}

                {hasCount && (
                  <div className="search-result-count" style={{ fontSize: "13px", color: "#5a6478", marginBottom: "8px" }}>
                    {count} question{count === 1 ? "" : "s"}
                  </div>
                )}

                {isZeroCount && (
                  <div className="search-result-zero-warning" style={{ fontSize: "13px", color: "#666", marginBottom: "12px" }}>
                    {zeroCountWarning}
                  </div>
                )}

                {/* Example Questions Section */}
                <Card className="detail-section">
                  <Card.Header
                    className="section-header"
                    onClick={() => toggleResultQuestions(result)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="section-header-content">
                      <img src={isExpanded ? arrow_down : arrow_right} alt="arrow" style={{ width: "10px" }} />
                      <span className="search-results-view-questions">View Questions</span>
                    </div>
                  </Card.Header>

                  {isExpanded && (
                    <Card.Body style={{ padding: 0, backgroundColor: colors.milkGlass500 }}>
                      {isLoading ? (
                        <div style={{ textAlign: "center", padding: "32px" }}>
                          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                          <p style={{ marginTop: "16px" }}>Loading questions...</p>
                        </div>
                      ) : questions.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "32px" }}>
                          {isZeroCount
                            ? zeroCountWarning
                            : `No questions available for this ${entityType}.`}
                        </div>
                      ) : (
                        <ExampleQuestionsCarousel
                          questions={questions}
                          total={questionsTotal}
                          onLoadMore={() => loadMoreQuestionsForResult(result)}
                        />
                      )}
                    </Card.Body>
                  )}
                </Card>
              </div>
            );
          }

          return (
            <div key={index} className="search-result-card">
              {/* Result Header */}
              <div className="standard-header">
                <div className="standard-badge-container">
                  <div className="standard-type-badge" style={{ padding: 0 }}>
                    <span className="standard-code" onClick={(e) => handleViewStandard(result, e, index + 1)}>
                      {getEntityTypeLabel(result.entity_type)} - {result.code}
                    </span>
                    <span className="standard-title-sm" onClick={(e) => handleViewStandard(result, e, index + 1)}>{result.name}</span>
                  </div>
                </div>
              </div>

              {/* Description Section */}
              {result.description && (
                <div className="description-section">
                  <p className="description-text">{result.description}</p>
                </div>
              )}

              {/* Example Questions Section */}
              <Card className="detail-section">
                <Card.Header
                  className="section-header"
                  onClick={() => toggleResultQuestions(result)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="section-header-content">
                    <img src={isExpanded ? arrow_down : arrow_right} alt="arrow" style={{ width: "10px" }} />
                    <span className="search-results-view-questions">View Questions</span>
                  </div>
                </Card.Header>

                {isExpanded && (
                  <Card.Body style={{ padding: 0, backgroundColor: colors.milkGlass500 }}>
                    {isLoading ? (
                      <div style={{ textAlign: "center", padding: "32px" }}>
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                        <p style={{ marginTop: "16px" }}>Loading questions...</p>
                      </div>
                    ) : questions.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "32px" }}>
                        No questions available for this {result.entity_type}.
                      </div>
                    ) : (
                      <ExampleQuestionsCarousel
                        questions={questions}
                        total={questionsTotal}
                        onLoadMore={() => loadMoreQuestionsForResult(result)}
                      />
                    )}
                  </Card.Body>
                )}
              </Card>
            </div>
          );
        })
      )}
    </div>
  );
};
